import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface PaymentItem {
  readonly transactionId: string;
  readonly claimNumber: string;
  readonly amount: number;
  readonly paymentDate: string;
  readonly status: string;
  readonly utrNumber: string | null;
  readonly paymentMode: string;
}

/** L15.09 — Vendor Payments: list of payments made to the vendor. */
export function VendorPaymentsPage(): ReactNode {
  const { token } = useAuth();
  const [items, setItems] = useState<readonly PaymentItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: readonly PaymentItem[] }>('/v1/vendor/payments', { token })
      .then(r => { if (alive) setItems(r.items); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <div style={{ padding: 24 }}>
      <h1>My Payments</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!items && !error && <p>Loading...</p>}
      {items && items.length === 0 && <p>No payments found.</p>}
      {items && items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f8fa', borderBottom: '1px solid #d0d7de' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Claim #</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Amount (₹)</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Mode</th>
              <th style={{ padding: 8, textAlign: 'left' }}>UTR</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={p.transactionId ?? i} style={{ borderBottom: '1px solid #d0d7de' }}>
                <td style={{ padding: 8 }}>{p.claimNumber}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{p.amount.toLocaleString('en-IN')}</td>
                <td style={{ padding: 8 }}>{p.paymentMode}</td>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{p.utrNumber ?? '—'}</td>
                <td style={{ padding: 8 }}>{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '—'}</td>
                <td style={{ padding: 8 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: p.status === 'CREDITED' ? '#dafbe1' : p.status === 'FAILED' ? '#ffebe9' : '#fff8c5', color: p.status === 'CREDITED' ? '#1a7f37' : p.status === 'FAILED' ? '#cf222e' : '#9a6700' }}>{p.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
